import gql from 'graphql-tag';
import { useInView, motion } from 'motion/react';
import { AnimMode, AnimVals } from "@/utils/animUtils";
import { ListInducteesFragment } from '@graphqlTypes';
import { getHighlightMarkup, getLinkData, sortInductees } from '@/core/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useViewSize } from '@/providers/ViewSizeContext';
import { MouseProvider } from '@/providers/MouseContext';
import { usePostType } from '@/hooks/postType';
import { Button, Container, TypePill, HeadlineAnim, RevealWrapper } from '@components';

import InducteesListInductee from './Inductee';

import className from 'classnames/bind';
import styles from './ListInductees.module.scss';
import { usePageWrapperData } from '@/providers/PageWrapperContext';
import { useClientMount } from '@/providers/ClientMountProvider';
const cx = className.bind(styles);

type ListInducteesProps = ListInducteesFragment & {
  listInductees: ListInductees;
};

export default function ListInductees(props: ListInducteesProps) {
  const {
    headline: defaultHeadline,
    listInductees,
    nomineeSeasonHeadline,
    cta,
    nomineeVariant,
    nominees
  } = props;
  const { siteSettings } = usePageWrapperData();
  const { isDesktop, viewWidth } = useViewSize();
  const inducteeListEl = useRef<HTMLDivElement>(null);
  const measureEl = useRef<HTMLParagraphElement>(null);
  const [fontSize, setFontSize] = useState<number>(148);
  const headline = siteSettings?.nomineeSeason ? nomineeSeasonHeadline : defaultHeadline;
  const headlineMarkup = getHighlightMarkup(headline);
  const ctaData = getLinkData(cta);
  const postType = usePostType({ node: { __typename: 'Ceremony' } });
  const { mounted } = useClientMount();

  const inductees = useMemo(() => {
    if (nomineeVariant) return nominees;
    return sortInductees(listInductees);
  }, [listInductees, nominees]);
  const longestInducteeTitle = useMemo(() => inductees.reduce((longest, inductee) => {
    const title = inductee.__typename === 'Inductee' ? inductee.title : inductee.name;
    return title.length > longest.length ? title : longest;
  }, ''), [inductees]);

  // -------------------- Measure Font Size

  useEffect(() => {
    if (!inducteeListEl.current || !measureEl.current) return;
    const container = inducteeListEl.current;
    const maxWidth = container.offsetWidth - 32;
    let size = 148;

    while (size > 12) {
      measureEl.current.style.fontSize = `${size / 8}rem`;
      if (measureEl.current.offsetWidth <= maxWidth) break;
      size -= 1;
    }

    setFontSize(size / 8);
  }, [longestInducteeTitle, inducteeListEl.current, viewWidth]);

  // -------------------- Animate in

  const rootEl = useRef<HTMLDivElement>(null);
  const isInView = useInView(rootEl, { amount: 0.1, once: AnimVals.once });

  // Animation values - always defined for SSR consistency
  const ANIM_OPACITY = AnimVals.opacity;
  const ANIM_DIST = AnimVals.sm.distance;
  const ANIM_DUR = AnimVals.sm.duration;
  const ANIM_DELAY = AnimVals.sm.delay;
  const ANIM_EASE = AnimVals.ease;

  const motionPropsConfig = useMemo(() => {
    if (!mounted) return;
    const isRevealEnabled = isDesktop && AnimMode !== 'none';
    let animInDelay = 0;

    const createMotionProps = (delay: number = 0) => {
      if (!isRevealEnabled) {
        return {
          initial: { opacity: 1, y: 0 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0 }
        };
      }
      
      return {
        initial: { opacity: ANIM_OPACITY, y: ANIM_DIST },
        animate: isInView ? { opacity: 1, y: 0 } : { opacity: ANIM_OPACITY, y: ANIM_DIST },
        transition: { duration: ANIM_DUR, ease: ANIM_EASE, delay }
      };
    };

    const pill = createMotionProps(animInDelay);
    animInDelay += ANIM_DELAY;

    const headlineDelay = animInDelay;
    animInDelay += ANIM_DELAY;

    animInDelay += inductees.length * ANIM_DELAY;

    const cta = createMotionProps(animInDelay);

    return {
      pill,
      headlineDelay,
      cta,
      isRevealEnabled
    };
  }, [isDesktop, isInView, inductees.length, mounted]);

  const { pill: motionProps_Pill, headlineDelay: headlineAnimInDelay, cta: motionProps_Cta, isRevealEnabled } = motionPropsConfig || {};

  // -------------------- Render

  return (
    <Container ref={rootEl} tag='section' className={cx(['list-inductees'])}>
    <RevealWrapper isInView={isInView}>

      <div className={cx('header')}>

        { postType?.key && (
          <motion.div {...motionProps_Pill}>
            <TypePill type={postType?.key} copy={postType?.label} />
          </motion.div>
        )}

        <HeadlineAnim
          tag="h2"
          text={headlineMarkup}
          isCenterAlign={true}
          className={cx(["title-uc-l"])}
          isRevealEnabled={true}
          isInView={isInView}
          animInDelay={headlineAnimInDelay}
        />

      </div>

      <div className={cx('inductee-list')}
        ref={inducteeListEl}
        style={{ '--font-size': `${fontSize}rem` } as React.CSSProperties}
      >
        <p className={cx("text-measure")} ref={measureEl}>{longestInducteeTitle}</p>
        <MouseProvider>
          {inductees.map((inductee, index) => (
            <InducteesListInductee inductee={inductee} index={index} isRevealEnabled={isRevealEnabled} isInView={isInView} key={index} />
          ))}
        </MouseProvider>
      </div>

      <motion.div className={cx('button-row')} {...motionProps_Cta}>
        { ctaData && <Button className={cx('cta')} {...ctaData} /> }
      </motion.div>

    </RevealWrapper>
    </Container>
  );

}

ListInductees.fragments = {
  entry: gql`
    fragment ListInductees on FlexiblePageContentContentListInducteesLayout {
      headline
      nomineeSeasonHeadline
      nomineeVariant
      inducteeClass {
        nodes {
          taxonomyName
          slug
          name
          databaseId
        }
      }
      nominees {
        name
        image {
          ...ACFMedia
        }
        link {
          linkType
          externalLink
          page {
            node {
              uri
            }
          }
        }
      }
      cta {
        linkType
        label
        externalLink
        page {
          node {
            uri
          }
        }
      }
    }
  `,
};
